import * as Sentry from "@sentry/react";
import axiosInstance from "./axiosConfig";
import { DataDocs } from "../types/tables-data";
import {
  GenerateDraftParams,
  GeneratePacketParams
} from "../types/drafts.type";
import { VISAVALUE } from "../types/visa.type";

export const getLoginAsToken = async (targetUserId: string) => {
  try {
    const response = await axiosInstance.post("/admin/generate_token", {
      userId: targetUserId
    });
    return response.data;
  } catch (error) {
    Sentry.captureException(error);
    throw error;
  }
};

export const extractInfoFromNIWEvidence = async (
  individualId: string,
  exhibits: string[],
  ids: string[]
) => {
  try {
    const response = await axiosInstance.post(
      "/extract_evidence_for_individual",
      {
        individualId,
        exhibits,
        ids,
        useAsync: true
      }
    );
    return response.data;
  } catch (error) {
    Sentry.captureException(error);
    throw error;
  }
};

export const summarizeResumeWithUpdate = async (
  individualId: string,
  firebasePath: string
) => {
  try {
    const response = await axiosInstance.post(
      "/summarize_candidate_resume_by_individual",
      {
        firebasePath,
        individualId
      }
    );
    return response.data;
  } catch (error) {
    Sentry.captureException(error);
    return null;
  }
};

export const summarizeExpertResume = async (firebase_path: string) => {
  try {
    const response = await axiosInstance.post("/summarize_expert_resume", {
      firebase_path
    });
    return response.data;
  } catch (error) {
    Sentry.captureException(error);
    return null;
  }
};

export const generateDraft = async ({
  individualId,
  draftType,
  expertId = null,
  tone = null,
  length = null,
  creativity = null,
  greeting = null,
  useAsync = false,
  referenceResume = false,
  isSupportLetter = false,
  templatePath = undefined
}: GenerateDraftParams): Promise<any> => {
  try {
    const requestBody = {
      individualId,
      draftType,
      expertId,
      ...(tone && { tone }),
      ...(length && { length }),
      ...(creativity && { creativity }),
      ...(greeting && { greeting }),
      ...(useAsync && { useAsync }),
      ...(referenceResume && { referenceResume }),
      ...(isSupportLetter && { isSupportLetter }),
      ...(templatePath && { templatePath })
    };
    const response = await axiosInstance.post("/create_draft", requestBody);
    return response.data;
  } catch (error) {
    Sentry.captureException(error);
    return null;
  }
};

export const generatePacket = async ({
  individualId,
  useAsync = false,
  packetName,
  divider,
  pagination
}: GeneratePacketParams): Promise<any> => {
  try {
    const requestBody = {
      individualId,
      ...(useAsync && { useAsync }),
      ...(packetName && { packetName }),
      ...(divider && { divider }),
      ...(pagination && { pagination })
    };

    const response = await axiosInstance.post(
      "/generate_case_packet",
      requestBody
    );
    return response.data;
  } catch (error) {
    Sentry.captureException(error);
    return null;
  }
};

export const getFirebaseUsers = async (
  orderBy = "",
  uid = "",
  email = "",
  maxResults = -1,
  pageToken = ""
) => {
  try {
    let paramString = "?";

    if (orderBy !== "") {
      paramString += `orderBy=${orderBy}`;
    }

    if (uid !== "") {
      paramString += `&uid=${uid}`;
    }

    if (email !== "") {
      paramString += `&email=${encodeURIComponent(email)}`;
    }

    if (maxResults !== -1) {
      paramString += `&maxResults=${maxResults}`;
    }

    if (pageToken !== "") {
      paramString += `&pageToken=${pageToken}`;
    }

    const response = await axiosInstance.get(`/admin/users${paramString}`);
    return response.data;
  } catch (error) {
    Sentry.captureException(error);
    throw error;
  }
};

export const updateFirebaseUser = async (uid: string, updates: object) => {
  try {
    // Send the request with only the provided parameters
    const response = await axiosInstance.post(`/admin/users/${uid}`, updates);
    return response.data;
  } catch (error) {
    Sentry.captureException(error);
    throw error;
  }
};

export const autoGenerateTopics = async (
  uid: string,
  topicId?: string,
  researchCategories?: string[]
) => {
  try {
    const requestBody: Record<string, any> = {
      individualId: uid,
      useAsync: true
    };

    if (topicId) requestBody.topicId = topicId;
    if (Array.isArray(researchCategories) && researchCategories.length > 0) {
      requestBody.researchCategories = researchCategories;
    }

    const response = await axiosInstance.post(
      "/perform_case_research",
      requestBody
    );
    return response.data;
  } catch (error) {
    Sentry.captureException(error);
    return null;
  }
};

export const downloadFile = async (storagePath: string) => {
  try {
    const response = await axiosInstance.get("/download_file", {
      params: {
        storagePath // Include storagePath as a query parameter
      },
      responseType: "blob"
    });
    return response.data;
  } catch (error) {
    Sentry.captureException(error);
    throw error;
  }
};

export const autoFillQuestionnaireWithDocuments = async (
  documents: DataDocs[],
  uid: string
) => {
  const documentsPaths = documents.map((doc) => doc.docRef);
  const requestBody = {
    individualId: uid,
    document_paths: documentsPaths,
    useAsync: true
  };
  const response = await axiosInstance.post(
    "/autofill-questionnaire",
    requestBody
  );
};

export const applyExhibitFormat = async (
  individualId: string,
  formatId: string
): Promise<any> => {
  try {
    const response = await axiosInstance.post("/apply_exhibit_format", {
      individualId,
      useAsync: true,
      exhibit_list_format_id: formatId
    });
    return response.data;
  } catch (error) {
    Sentry.captureException(error);
    throw error;
  }
};

export const getSupportScripts = async () => {
  try {
    const response = await axiosInstance.get("support_scripts");
    return response.data;
    return null;
  } catch (error) {
    Sentry.captureException(error);
    throw error;
  }
};

export const runSupportScript = async (
  name: string,
  args: Record<string, any>
): Promise<any> => {
  try {
    const payload: Record<string, any> = {
      name,
      useAsync: true
    };

    for (const [key, value] of Object.entries(args)) {
      if (value !== "") {
        payload[key] = value;
      }
    }

    const response = await axiosInstance.post("/support_scripts/run", payload);
    return response.data;
  } catch (error) {
    Sentry.captureException(error);
    throw error;
  }
};
